import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { NAV_TOP_SAFE_PADDING } from '../components/NavBar';
import Footer from '../components/Footer';
import {
  BadgeCheck, Percent, MessageCircle, Handshake,
  ShieldCheck, Info, User
} from 'lucide-react';
import './HomePage.css';

const HomePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const toggleLang = () => i18n.changeLanguage(i18n.language.startsWith('ko') ? 'en' : 'ko');

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="home-page">

      {/* ── Header ── */}
      {/* paddingTop: 일부 안드로이드 기기의 분할화면/플로팅윈도우 시스템 바가
          화면 맨 위와 겹쳐 로그인/시작하기 버튼 터치를 가로채는 문제 방지 */}
      <header className="home-header" style={{ paddingTop: NAV_TOP_SAFE_PADDING }}>
        <nav className="container">
          <Link to="/" className="logo">freematch</Link>
          <ul className="nav-links">
            <li><a href="#features">{t('home.navFeatures')}</a></li>
            <li><a href="#how-it-works">{t('home.navHow')}</a></li>
            <li><a href="#contact">{t('home.navContact')}</a></li>
          </ul>
          <div className="auth-buttons">
            <button onClick={toggleLang} style={{
              padding: '6px 12px', borderRadius: 7, fontSize: 13, fontWeight: 700,
              background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)',
              border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {i18n.language.startsWith('ko') ? 'EN' : '한국어'}
            </button>
            {!user ? (
              <>
                <Link to="/login" className="btn-login">{t('nav.login')}</Link>
                <Link to="/register" className="btn-signup">{t('nav.getStarted')}</Link>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', background: '#F5F3FF', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#4F46E5' }}>
                  <User size={15} />
                  <span>{t('home.userGreeting', { name: user.nickname || user.name })}</span>
                </div>
                <button onClick={() => navigate('/dashboard')} className="btn-home-action green">{t('home.dashboard')}</button>
                <button onClick={handleLogout} className="btn-home-action red">{t('home.logout')}</button>
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* ── Hero ── */}
      <main className="home-main">
        <div className="hero">
          <div className="hero-badge">
            <BadgeCheck size={14} />
            {t('home.badge')}
          </div>
          <h1>
            {t('home.heroTitle1')}<br />
            <span>{t('home.heroTitle2')}</span>
          </h1>
          <p className="hero-subtitle">
            {t('home.heroDesc1')}<br />
            {t('home.heroDesc2')}
          </p>
          <div className="button-group">
            <Link to="/services" className="btn-primary">{t('home.findExperts')}</Link>
            <Link to="/projects" className="btn-secondary">{t('home.postProject')}</Link>
          </div>

          <div className="hero-stats">
            <div className="hero-stat-item">
              <div className="hero-stat-number">0%</div>
              <div className="hero-stat-label">{t('home.stat0')}</div>
            </div>
            <div className="hero-stat-item">
              <div className="hero-stat-number">{t('home.statRealtime')}</div>
              <div className="hero-stat-label">{t('home.statChat')}</div>
            </div>
            <div className="hero-stat-item">
              <div className="hero-stat-number">{t('home.statDirect')}</div>
              <div className="hero-stat-label">{t('home.statVerified')}</div>
            </div>
          </div>
        </div>
      </main>

      {/* ── Features ── */}
      <section className="features" id="features">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <span className="section-tag">WHY FREEMATCH</span>
          </div>
          <h2>{t('home.whyTitle')}</h2>
          <p className="section-desc">{t('home.whyDesc')}</p>

          <div className="features-grid">
            <div className="feature-card highlight">
              <div className="feature-icon-wrap"><Percent size={20} /></div>
              <h3>{t('home.feat1T')}</h3>
              <p>{t('home.feat1D')}</p>
              <div className="feature-compare">
                <span className="compare-item bad">{t('home.compareBad')}</span>
                <span className="compare-item good">freematch 0%</span>
              </div>
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrap"><MessageCircle size={20} /></div>
              <h3>{t('home.feat2T')}</h3>
              <p>{t('home.feat2D')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrap"><Handshake size={20} /></div>
              <h3>{t('home.feat3T')}</h3>
              <p>{t('home.feat3D')}</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon-wrap"><ShieldCheck size={20} /></div>
              <h3>{t('home.feat4T')}</h3>
              <p>{t('home.feat4D')}</p>
            </div>
          </div>

          {/* 직접거래 안내 */}
          <div className="direct-trade-notice">
            <div className="notice-icon"><Info size={18} /></div>
            <div className="notice-body">
              <h3>{t('home.directTitle')}</h3>
              <p>
                {t('home.directDesc')}
              </p>
              <div className="notice-points">
                <div className="notice-point"><BadgeCheck size={14} /><span>{t('home.point1')}</span></div>
                <div className="notice-point"><BadgeCheck size={14} /><span>{t('home.point2')}</span></div>
                <div className="notice-point"><BadgeCheck size={14} /><span>{t('home.point3')}</span></div>
                <div className="notice-point"><BadgeCheck size={14} /><span>{t('home.point4')}</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <span className="section-tag">HOW IT WORKS</span>
          </div>
          <h2>{t('home.navHow')}</h2>
          <p className="section-desc">{t('home.howDesc')}</p>
          <div className="steps">
            {[
              { n: '01', title: t('home.step1T'), desc: t('home.step1D') },
              { n: '02', title: t('home.step2T'), desc: t('home.step2D') },
              { n: '03', title: t('home.step3T'), desc: t('home.step3D') },
              { n: '04', title: t('home.step4T'), desc: t('home.step4D') },
              { n: '05', title: t('home.step5T'), desc: t('home.step5D') },
              { n: '06', title: t('home.step6T'), desc: t('home.step6D') },
            ].map((step, i, arr) => (
              <React.Fragment key={step.n}>
                <div className="step">
                  <div className="step-number">{step.n}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
                {i < arr.length - 1 && <div className="step-arrow">→</div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── 면책 안내 ── */}
      <section className="disclaimer-section">
        <div className="container">
          <div className="disclaimer-box">
            <div className="disclaimer-box-icon">⚠</div>
            <div>
              <h3>{t('home.noticeTitle')}</h3>
              <ul>
                <li>{t('home.notice1')}</li>
                <li>{t('home.notice2')}</li>
                <li>{t('home.notice3')}</li>
                <li>{t('home.notice4')}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI 에이전트 도구 안내 (V4.0, 건당/묶음 결제 — 구독 아님) ── */}
      <section className="pro-section">
        <div className="container">
          <div className="pro-box">
            <div className="pro-box-text">
              <h3>{t('home.membershipTitle')}</h3>
              <p>{t('home.membershipDesc')}</p>
            </div>
            <Link to="/services" className="pro-btn">{t('home.membershipBtn')}</Link>
          </div>
        </div>
      </section>

      {/* ── Footer (사업자 정보 포함, components/Footer.tsx) ── */}
      <Footer />

    </div>
  );
};

export default HomePage;
